<template>
  <div class="home-banners">
    <div
      v-for="banner in banners"
      :key="banner.productId"
      :class="['banner-item', banner.type, viewMode]"
      @click="goToProduct(banner.productId)"
    >
      <!-- 图片 -->
      <img
        :src="currentImg(banner)"
        :alt="banner.title"
        class="banner-img"
        @load="onImgLoad(banner.productId, $event)"
      />

      <!-- 遮罩 -->
      <div class="overlay" :class="brightnessClass[banner.productId]"></div>

      <!-- 顶部文字区 -->
      <div class="top-text">
        <h2 class="title">{{ banner.title }}</h2>
        <p v-if="banner.subTitle && !isMobile" class="subtitle">
          {{ banner.subTitle }}
        </p>
      </div>

      <!-- 底部文字 + 按钮 -->
      <div class="bottom-text">
        <p v-if="banner.footText && !isMobile" class="foot-text">
          {{ banner.footText }}
        </p>

        <!-- Desktop/Pad 按钮 -->
        <div v-if="!isMobile" class="btn-group">
          <button
            class="btn primary"
            @click.stop="goToProduct(banner.productId)"
          >
            See More
          </button>
          <button class="btn outline" @click.stop="scrollToInquiry">
            Inquiry
          </button>
        </div>

        <!-- Mobile 按钮（并排） -->
        <div v-else class="btn-group-mobile">
          <button
            class="btn m-primary"
            @click.stop="goToProduct(banner.productId)"
          >
            See More
          </button>
          <button class="btn m-outline" @click.stop="scrollToInquiry">
            Inquiry
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getHomeBanners } from "@/api/productAPI";

export default {
  name: "HomeBanners",
  data() {
    return {
      banners: [],
      isMobile: false,
      viewMode: "desktop", // desktop / tablet / mobile
      brightnessClass: {},
      resizeTimer: null,
    };
  },
  async created() {
    this.banners = await getHomeBanners();
    this.updateViewMode();
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.updateViewMode();
      }, 200); // 防抖
    },

    updateViewMode() {
      const w = window.innerWidth;
      if (w < 768) {
        this.viewMode = "mobile";
        this.isMobile = true;
      } else if (w < 992) {
        this.viewMode = "tablet";
        this.isMobile = false;
      } else {
        this.viewMode = "desktop";
        this.isMobile = false;
      }
    },

    currentImg(banner) {
      if (this.viewMode === "mobile") return banner.imgUrlMobile;

      if (banner.type === "full") return banner.imgUrl; // 横图
      if (banner.type === "half") return banner.imgUrlMobile; // 竖图

      return banner.imgUrl;
    },

    onImgLoad(productId, event) {
      const img = event.target;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);

      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let sum = 0;

      for (let i = 0; i < data.length; i += 4) {
        sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
      }

      const avg = sum / (data.length / 4);

      this.$set(
        this.brightnessClass,
        productId,
        avg > 170 ? "bright" : avg < 90 ? "dark" : "normal"
      );
    },

    goToProduct(id) {
      this.$router.push(`/product/${id}`);
    },

    scrollToInquiry() {
      const target = () => {
        const dom = document.getElementById("inquiry-form");
        if (dom) dom.scrollIntoView({ behavior: "smooth" });
        else setTimeout(target, 200);
      };

      if (this.$route.path === "/") {
        target();
      } else {
        this.$router.push("/").then(() => {
          this.$nextTick(() => setTimeout(target, 400));
        });
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";

.home-banners {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

/* Banner 容器 */
.banner-item {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  margin: 10px;
  cursor: pointer;

  /* --- 🔥 进入页面时淡入 & 上浮 --- */
  opacity: 0;
  transform: translateY(24px);
  animation: fadeFloatIn 0.9s ease forwards;

  /* 横图（前 4 张，PC/Pad） */
  &.full.desktop,
  &.full.tablet {
    flex: 0 1 calc(100% - 20px);
    aspect-ratio: 16/9;
  }

  /* 竖图（后 6 张，PC/Pad） */
  &.half.desktop,
  &.half.tablet {
    flex: 0 1 calc(50% - 20px);
    aspect-ratio: 3/4;
  }

  /* Mobile — 全部竖图 */
  &.mobile {
    flex: 0 1 calc(100% - 20px);
    aspect-ratio: 3/4;
  }

  /* 图片总是居中显示 */
  .banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    display: block;
    transition: transform 1.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  /* ===== 🔥 鼠标 Hover 轻微缩放（Apple Style） ===== */
  &:hover .banner-img {
    transform: scale(1.04);
  }

  /* 遮罩 */
  .overlay {
    position: absolute;
    inset: 0;
    transition: 0.35s ease;
    border-radius: inherit;

    &.bright {
      background: rgba(0, 0, 0, 0.4);
    }
    &.normal {
      background: rgba(0, 0, 0, 0.25);
    }
    &.dark {
      background: rgba(255, 255, 255, 0.12);
    }
  }

  /* ---- Hover 时遮罩轻微变化（更 Apple） ---- */
  &:hover .overlay {
    background: rgba(0, 0, 0, 0.18);
  }

  /* 文字区 — 上下两块布局 */
  .top-text,
  .bottom-text {
    position: absolute;
    width: 100%;
    padding: 20px 0;
    z-index: 3;
  }

  .top-text {
    top: 0;
    text-align: center;
  }

  .bottom-text {
    bottom: 0;
    text-align: center;
  }

  /* Title & SubTitle */
  .title {
    color: #fff;
    font-weight: 600;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.45);
    max-width: 80%;
    margin: 0 auto;

    &.desktop,
    &.tablet {
      font-size: 2rem;
      margin-bottom: 6px;
    }
  }

  .subtitle {
    color: #fff;
    opacity: 0.9;
    font-size: 1.1rem;
    margin-top: 4px;
  }

  /* FootText */
  .foot-text {
    color: #fff;
    opacity: 0.9;
    margin-bottom: 14px;
    max-width: 70%;
    margin-left: auto;
    margin-right: auto;
  }

  /* Desktop 按钮 */
  .btn-group {
    display: flex;
    justify-content: center;
    gap: 14px;

    .btn {
      padding: 10px 24px;
      border-radius: 30px;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.28s ease; /* 🔥 添加 hover 动效 */
    }

    .primary {
      background: @color-primary;
      color: #fff;
      border: none;
    }

    .outline {
      background: transparent;
      color: #fff;
      border: 2px solid #fff;
    }

    /* 🔥 Hover 动效 */
    .btn:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.28);
    }
  }

  /* Mobile 按钮 */
  .btn-group-mobile {
    display: flex;
    justify-content: center;
    gap: 10px;

    .btn {
      padding: 8px 16px;
      border-radius: 26px;
      font-size: 0.9rem;
      transition: all 0.28s ease;
    }

    .m-primary {
      background: @color-primary;
      color: #fff;
      border: none;
    }

    .m-outline {
      background: transparent;
      border: 1.5px solid #fff;
      color: #fff;
    }

    /* 🔥 Mobile 同样有 hover 动效 */
    .btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
  }
}

/* ====== ✨ 淡入 & 浮动动画 ====== */
@keyframes fadeFloatIn {
  0% {
    opacity: 0;
    transform: translateY(24px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
}
/* ============================
   Banner 排队入场延迟动画
   ============================ */

/* 每个 banner 延迟进入（0.12s 阶梯）*/
.home-banners .banner-item:nth-child(1) {
  animation-delay: 0s;
}
.home-banners .banner-item:nth-child(2) {
  animation-delay: 0.12s;
}
.home-banners .banner-item:nth-child(3) {
  animation-delay: 0.24s;
}
.home-banners .banner-item:nth-child(4) {
  animation-delay: 0.36s;
}
.home-banners .banner-item:nth-child(5) {
  animation-delay: 0.48s;
}
.home-banners .banner-item:nth-child(6) {
  animation-delay: 0.6s;
}
.home-banners .banner-item:nth-child(7) {
  animation-delay: 0.72s;
}
.home-banners .banner-item:nth-child(8) {
  animation-delay: 0.84s;
}
.home-banners .banner-item:nth-child(9) {
  animation-delay: 0.96s;
}
.home-banners .banner-item:nth-child(10) {
  animation-delay: 1.08s;
}

/* ============================
   文字分别淡入动画（比图片晚）
   ============================ */

.banner-item .top-text,
.banner-item .bottom-text {
  opacity: 0;
  transform: translateY(10px);
  animation: fadeText 0.9s ease forwards;
  animation-delay: 0.45s; /* 文字比图片晚出现 */
}

/* 让 title 更早一点出现 */
.banner-item .top-text .title {
  animation: fadeText 0.9s ease forwards;
  animation-delay: 0.35s;
}

/* 副标题比 title 再稍晚 */
.banner-item .top-text .subtitle {
  animation: fadeText 0.9s ease forwards;
  animation-delay: 0.45s;
}

/* 底部文字 */
.banner-item .bottom-text .foot-text {
  animation: fadeText 0.9s ease forwards;
  animation-delay: 0.55s;
}

/* 底部按钮组 */
.banner-item .bottom-text .btn-group,
.banner-item .bottom-text .btn-group-mobile {
  opacity: 0;
  transform: translateY(12px);
  animation: fadeText 0.9s ease forwards;
  animation-delay: 0.65s;
}

/* 文字淡入动画 */
@keyframes fadeText {
  0% {
    opacity: 0;
    transform: translateY(12px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
